import React, { useState, useRef, useCallback } from 'react';
import { Camera, Square, Play, Pause, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';

interface VideoRecorderProps {
  onVideoCapture: (file: File) => Promise<void>;
  disabled?: boolean;
}

export const VideoRecorder: React.FC<VideoRecorderProps> = ({
  onVideoCapture,
  disabled = false,
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordedVideo, setRecordedVideo] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  
  const { toast } = useToast();

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 1280, height: 720 },
        audio: true,
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      toast({
        title: "Camera Error",
        description: "Unable to access camera. Please check permissions.",
        variant: "destructive",
      });
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const startRecording = useCallback(async () => {
    if (!streamRef.current) {
      await startCamera();
    }

    if (!streamRef.current) return;

    try {
      const mediaRecorder = new MediaRecorder(streamRef.current, {
        mimeType: 'video/webm;codecs=vp9',
      });

      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        const videoUrl = URL.createObjectURL(blob);
        setRecordedVideo(videoUrl);
        stopCamera();
      };

      mediaRecorder.start();
      setIsRecording(true);
      setIsPaused(false);
    } catch (error) {
      console.error('Error starting recording:', error);
      toast({
        title: "Recording Error",
        description: "Unable to start recording.",
        variant: "destructive",
      });
    }
  }, [toast]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
    }
  }, [isRecording]);

  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      if (isPaused) {
        mediaRecorderRef.current.resume();
        setIsPaused(false);
      } else {
        mediaRecorderRef.current.pause();
        setIsPaused(true);
      }
    }
  }, [isRecording, isPaused]);

  const handleUpload = async () => {
    if (!recordedVideo) return;

    setUploading(true);
    try {
      const blob = new Blob(chunksRef.current, { type: 'video/webm' });
      const file = new File([blob], `video_${Date.now()}.webm`, { type: 'video/webm' });
      
      await onVideoCapture(file);
      
      // Clean up
      URL.revokeObjectURL(recordedVideo);
      setRecordedVideo(null);
      chunksRef.current = [];
      
      toast({
        title: "Success",
        description: "Video uploaded successfully!",
      });
    } catch (error) {
      console.error('Error uploading video:', error);
      toast({
        title: "Upload Error",
        description: "Failed to upload video.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const retakeVideo = () => {
    if (recordedVideo) {
      URL.revokeObjectURL(recordedVideo);
      setRecordedVideo(null);
    }
    chunksRef.current = [];
    startCamera();
  };

  return (
    <div className="space-y-4">
      <div className="relative aspect-video bg-muted rounded-lg overflow-hidden">
        {recordedVideo ? (
          <video
            src={recordedVideo}
            controls
            className="w-full h-full object-cover"
          />
        ) : (
          <video
            ref={videoRef}
            autoPlay
            muted
            className="w-full h-full object-cover"
          />
        )}
        
        {isRecording && (
          <div className="absolute top-4 left-4 flex items-center gap-2 bg-destructive text-destructive-foreground px-3 py-1 rounded-full">
            <div className="w-2 h-2 bg-current rounded-full animate-pulse" />
            <span className="text-sm font-medium">
              {isPaused ? 'PAUSED' : 'RECORDING'}
            </span>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-1">
        {!recordedVideo ? (
          <>
            {!isRecording ? (
              <Button
                onClick={startRecording}
                disabled={disabled}
                variant="default"
                size="sm"
                className="w-full text-xs"
              >
                <Camera className="w-3 h-3 mr-1" />
                Record
              </Button>
            ) : (
              <div className="flex gap-1">
                <Button
                  onClick={pauseRecording}
                  disabled={disabled}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                >
                  {isPaused ? <Play className="w-3 h-3" /> : <Pause className="w-3 h-3" />}
                </Button>
                <Button
                  onClick={stopRecording}
                  disabled={disabled}
                  variant="destructive"
                  size="sm"
                  className="flex-1"
                >
                  <Square className="w-3 h-3" />
                </Button>
              </div>
            )}
          </>
        ) : (
          <>
            <Button
              onClick={handleUpload}
              disabled={disabled || uploading}
              variant="default"
              size="sm"
              className="w-full text-xs"
            >
              {uploading ? (
                <>
                  <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></span>
                  Upload
                </>
              ) : (
                <>
                  <Upload className="w-3 h-3 mr-1" />
                  Upload
                </>
              )}
            </Button>
            <Button
              onClick={retakeVideo}
              disabled={disabled || uploading}
              variant="outline"
              size="sm"
              className="w-full text-xs"
            >
              <Camera className="w-3 h-3 mr-1" />
              Retake
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default VideoRecorder;